import { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Archive,
    Activity,
    AlarmClock,
    BarChart3,
    Bot,
    Boxes,
    Handshake,
    Briefcase,
    Building2,
    CalendarCog,
    Cloud,
    CreditCard,
    FileSearch,
    FolderKanban,
    Folders,
    Globe,
    Flame,
    LayoutDashboard,
    Mail,
    Network,
    Package,
    RefreshCw,
    Scale,
    BookOpenCheck,
    ShoppingBag,
    Smartphone,
    Users,
    Wallet,
    Images,
    CalendarClock,
    UserPlus,
    Wrench,
    Map as MapIcon, Receipt,
    Layers,
} from "lucide-react";

import { authenticationAtom } from "../State";
import { configuration } from "../Configuration";
import { userAPI } from "../UserAPI";
import { fetchGuidelineAccess } from "../guidelines/GuidelinesAPI";
import { GUIDELINES_PATH } from "../guidelines/guidelinesPath";
import { useRoleAccess } from "./useRoleAccess";
import { configuredPortalPanels, dropEmptyHeadings, restrictToPortalPanels, sectionForMovedPanel } from "./settingsPanels";
import { ItemType, SideBarItem } from "../components/SideBar";
import type { AgentTabKey } from "../settings/agentTabs";
import { FILTER_PARAMS } from "../settings/marketplaceFilters";
import type { GuideId } from "../guide/guideIds";
import { PUSH_DEVICES_PANEL_VISIBLE } from "@/settings/pushDevicesPanelVisibility";
import { BRAND } from "@/brand/brand";

// "Ausschreibungen" is gated on the customer having a tool provisioned from the
// tender MCP server. Raw tool ids from getCustomerTools look like
// "<serverId>/<toolName>" (e.g. "69fc5126b9e23f4aedbdb22c/tender.search"); we
// match on the server id (the part before the "/") so this stays stable if the
// tool names change.
const TENDER_MCP_SERVER_ID = "69fc5126b9e23f4aedbdb22c";
/** Wie lange ein „Nein" nach einem früheren „Ja" nachgeprüft wird: 4 × 15 s, über die ~45 s Werkzeug-Ladezeit nach einem Neustart. */
export const TENDER_ACCESS_RETRIES = 4;
export const TENDER_ACCESS_RETRY_MS = 15_000;
const tenderAccessKey = (customer: string) => `tri.tenderToolsAccess.${customer}`;
/** Das letzte „Ja" dieser Sitzung — Bequemlichkeit, ohne Speicher wird wie bisher erst gefragt. */
const readTenderAccess = (customer: string): boolean => {
    try {
        return window.sessionStorage.getItem(tenderAccessKey(customer)) === "1";
    } catch {
        return false;
    }
};
const writeTenderAccess = (customer: string, has: boolean) => {
    try {
        if (has) window.sessionStorage.setItem(tenderAccessKey(customer), "1");
        else window.sessionStorage.removeItem(tenderAccessKey(customer));
    } catch {
        /* Ohne Speicher gilt nur die Antwort dieses Aufrufs. */
    }
};

// Betreiberpflichten is gated on procurement: the provider tenant
// (S8QW-G8R2-9QLC) always, plus any tenant that has procured the
// "Betreiberverantwortung" offer (AgentToolset 6a255f8040d2b1e58e36c478)
// via a consumer order whose orderItems reference it.
const OPERATOR_OBLIGATIONS_OFFER_ID = "6a255f8040d2b1e58e36c478";
// Product Management is gated on procurement: the provider tenant always, plus
// any tenant that procured the "Produktdaten" toolset (AgentToolset
// 6a303278f82f726ee11a3718) via a consumer order whose orderItems reference it.
const PRODUCT_TOOLSET_OFFER_ID = "6a303278f82f726ee11a3718";
// Baselining is gated the same way: the provider tenant (owner of the offer) always, plus any tenant that procured
// the "FM-Baselining" toolset (AgentToolset 6ae5f6d128c5096f7e2b680b, tri-server seedBaseliningOffer.ts).
export const BASELINING_OFFER_ID = "6ae5f6d128c5096f7e2b680b";
const PROVIDER_TENANT = "S8QW-G8R2-9QLC";

export interface SettingsNavigation {
    items: SideBarItem[];
    /** Aktiver Panel-Key, nur auf /settings gesetzt. */
    selectedPanel: string | undefined;
    /** Öffnet ein Panel — von jeder Route aus (navigiert nach /settings). */
    openPanel: (key: string, params?: Record<string, string | null>) => void;
    hasOperatorObligationsAccess: boolean | undefined;
    hasProductManagementAccess: boolean | undefined;
    hasBaseliningAccess: boolean | undefined;
    hasTenderToolsAccess: boolean;
}

/**
 * Navigationsdaten der Einstellungs-Sidebar: Bereichsliste inklusive aller
 * Rollen- und Buchungs-Gates. Lebt im AppShell (einmal pro Layout), damit die
 * Sidebar beim Routenwechsel stehen bleibt; die Panels erhalten die
 * Access-Werte über den Outlet-Context statt eigener Doppel-Abfragen.
 */
export function useSettingsNavigation(): SettingsNavigation {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [authentication] = useAtom(authenticationAtom) as any;
    const roleAccess = useRoleAccess();
    const [hasOperatorObligationsAccess, setHasOperatorObligationsAccess] = useState<boolean | undefined>(undefined);
    const [hasProductManagementAccess, setHasProductManagementAccess] = useState<boolean | undefined>(undefined);
    const [hasBaseliningAccess, setHasBaseliningAccess] = useState<boolean | undefined>(undefined);
    const [hasTenderToolsAccess, setHasTenderToolsAccess] = useState(false);
    /** Richtlinien-Werkstatt: nur Mandanten, denen der Marktplatz-Eintrag freigegeben ist oder die ihn gekauft haben. */
    const [hasGuidelinesAccess, setHasGuidelinesAccess] = useState(false);

    const location = useLocation();
    const onSettings = location.pathname === "/settings";
    const onAdministration = location.pathname === "/administration";
    const onCbos = location.pathname.startsWith("/cbos");
    const onGuidelines = location.pathname.startsWith(GUIDELINES_PATH);
    const settingsPanel = searchParams.get("panel") || "userProfile";
    const selectedPanel = onSettings
        ? // Abrechnung und Statistiken sind Reiter der Bestellungen; ein alter Link markiert diese.
          sectionForMovedPanel(settingsPanel)
            ? "orders"
            : settingsPanel
        : onAdministration
          ? searchParams.get("panel") || "customerManagement"
          : onCbos
            ? location.pathname.split("/")[2] || "portfolio-dashboard"
            : onGuidelines
              ? "guidelines"
              : undefined;

    useEffect(() => {
        const customer = authentication?.customer;
        if (!customer) {
            setHasOperatorObligationsAccess(false);
            setHasProductManagementAccess(false);
            setHasBaseliningAccess(false);
            return;
        }
        if (customer === PROVIDER_TENANT) {
            setHasOperatorObligationsAccess(true);
            setHasProductManagementAccess(true);
            setHasBaseliningAccess(true);
            return;
        }
        setHasOperatorObligationsAccess(undefined);
        setHasProductManagementAccess(undefined);
        setHasBaseliningAccess(undefined);
        let cancelled = false;
        (async () => {
            try {
                const orders = await userAPI.getConsumerOrders(customer);
                const procuredFor = (offerId: string) =>
                    (orders || []).some((order: any) =>
                        (order?.orderItems || []).some(
                            (item: any) => String(item?.serviceReference) === offerId || String(item?.marketplaceOffer?._id) === offerId,
                        ),
                    );
                if (!cancelled) {
                    setHasOperatorObligationsAccess(procuredFor(OPERATOR_OBLIGATIONS_OFFER_ID));
                    setHasProductManagementAccess(procuredFor(PRODUCT_TOOLSET_OFFER_ID));
                    setHasBaseliningAccess(procuredFor(BASELINING_OFFER_ID));
                }
            } catch {
                // Keep access undefined on error; don't deny access due to transient failures
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [authentication?.customer]);

    useEffect(() => {
        const customer = authentication?.customer;
        if (!customer) {
            setHasGuidelinesAccess(false);
            return;
        }
        let cancelled = false;
        // Ein Netzfehler (null) lässt den letzten Stand stehen, statt den Eintrag zu verstecken.
        fetchGuidelineAccess(customer).then(visible => {
            if (!cancelled && visible !== null) setHasGuidelinesAccess(visible);
        });
        return () => {
            cancelled = true;
        };
    }, [authentication?.customer]);

    // „Nein" ist hier oft kein Nein: `getCustomerTools` antwortet auf einen Netzfehler mit `[]`, und
    // nach einem Serverneustart fehlen die MCP-Werkzeuge rund 45 s in der Liste. Daraus wurde
    // `false` — der Bereich „Ausschreibungen" verschwand aus der Seitenleiste, die Seite blieb leer
    // und das offene Panel samt getipptem Text war weg (Prod 22.10.2026). Deshalb: ein früheres „Ja"
    // dieser Sitzung gilt sofort, und ein „Nein" danach erst, wenn es sich über eine Minute hält.
    useEffect(() => {
        const customer = authentication?.customer;
        if (!customer) {
            setHasTenderToolsAccess(false);
            return;
        }
        const knownYes = readTenderAccess(customer);
        setHasTenderToolsAccess(knownYes);
        let cancelled = false;
        let timer: number | undefined;
        const check = async (attempt: number) => {
            let has = false;
            try {
                const tools = await userAPI.getCustomerTools(customer);
                has = (Array.isArray(tools) ? tools : []).some(
                    (tool: any) =>
                        String(tool?._id ?? tool?.id).split("/")[0] === TENDER_MCP_SERVER_ID,
                );
            } catch {
                // Wie ein „Nein" behandeln — unten entscheidet die Wiederholung.
            }
            if (cancelled) return;
            if (has) {
                writeTenderAccess(customer, true);
                setHasTenderToolsAccess(true);
            } else if (knownYes && attempt < TENDER_ACCESS_RETRIES) {
                timer = window.setTimeout(() => check(attempt + 1), TENDER_ACCESS_RETRY_MS);
            } else {
                writeTenderAccess(customer, false);
                setHasTenderToolsAccess(false);
            }
        };
        check(0);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [authentication?.customer]);

    /**
     * Den Bereich wechseln — aus der **aktuellen** Adresse, nicht aus der des Renderdurchlaufs.
     *
     * `searchParams` ist der Stand, den dieser Durchlauf gesehen hat. Die Tabellen der Bereiche
     * schreiben aber ebenfalls in die Adresse: Ihre Seitenzahl landet dort, sobald eine Tabelle
     * aufgebaut ist. Zwischen dem Rendern der Seitenleiste und dem Klick liegt also womöglich ein
     * fremder Schreibvorgang, und aus dem alten Stand gebaut nimmt der Wechsel ihn wieder zurück —
     * die Adresse nennt dann einen anderen Bereich als der Inhalt zeigt, und beide Schreiber
     * stoßen sich gegenseitig erneut an.
     *
     * `setSearchParams` mit einer Funktion liest den Stand im Augenblick des Schreibens. Denselben
     * Weg nehmen die Tabellen bereits (siehe `useUrlPageState`).
     */
    const openPanel = (key: string, params?: Record<string, string | null>) => {
        // Die Werkstatt ist eine eigene Seite, kein Bereich der Einstellungen.
        if (key === "guidelines") {
            navigate(GUIDELINES_PATH);
            return;
        }
        if (onCbos) {
            // The query string comes along: the project is chosen for the whole area, and dropping
            // it on a screen change would ask for it again on every hop.
            navigate({ pathname: `/cbos/${key}`, search: location.search });
            return;
        }
        if (onAdministration) {
            // Bestandsverhalten der Administration: übrige Parameter bleiben.
            setSearchParams(
                (current) => {
                    const next = new URLSearchParams(current);
                    next.set("panel", key);
                    return next;
                },
                { replace: true },
            );
            return;
        }
        // Innerhalb von /settings bleiben die übrigen URL-Parameter erhalten
        // (wie das bisherige setParams); der tab-Parameter wird beim Wechsel
        // gelöscht — außer beim Ziel processManagement, dessen Tabs ihn lesen.
        // `section` (der Reiter der Bestellungen) gehört nur dorthin und fällt immer.
        if (!onSettings) {
            // Von außerhalb: Es gibt keine Parameter zu bewahren, also ein voller Sprung.
            const fresh = new URLSearchParams();

            fresh.set("panel", key);
            Object.entries(params ?? {}).forEach(([name, value]) => value !== null && fresh.set(name, value));
            navigate(`/settings?${fresh.toString()}`);

            return;
        }

        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current);

                next.set("panel", key);
                next.delete("section");

                if (key !== "processManagement") {
                    next.delete("tab");
                }

                // Ziel-Reiter eines Unterpunkts (Agentendienste): kommt nach dem Löschen,
                // sonst nähme der Wechsel ihn gleich wieder zurück. `null` löscht einen Parameter.
                Object.entries(params ?? {}).forEach(([name, value]) => (value === null ? next.delete(name) : next.set(name, value)));

                return next;
            },
            { replace: true },
        );
    };

    // Benutzerprofil und Sicherheitsprofil erreichen Nutzer über das Kontomenü
    // unten links in der Sidebar — sie stehen deshalb nicht in der
    // Bereichsliste; ihre Panels bleiben über ?panel=… erreichbar.
    let items: SideBarItem[] = [
        {
            type: ItemType.heading,
            key: "customerHeading",
            text: t("settingsPanel.customerHeading"),
        },
    ];

    if (roleAccess.canSeeCompanyProfile) {
        items.push({
            type: ItemType.item,
            key: "companyProfile",
            text: t("settingsPanel.companyProfile"),
            icon: <Building2 />,
        });
    }

    if (roleAccess.canSeeTeamManagement) {
        items.push({
            type: ItemType.item,
            key: "teamManagement",
            text: t("settingsPanel.teamManagement"),
            icon: <Users />,
        });
    }

    // Push notifications of the tenant's users: same audience as the team management (customer administrators).
    if (PUSH_DEVICES_PANEL_VISIBLE && roleAccess.canSeeTeamManagement) {
        items.push({
            type: ItemType.item,
            key: "pushDevices",
            text: t("settingsPanel.pushDevices"),
            icon: <Smartphone />,
        });
    }

    // Abrechnung und Statistiken stehen nicht in der Liste: Sie sind Reiter der Bestellungen
    // (`OrdersSections`), und wer eines von beiden sehen darf, darf auch die Bestellungen sehen.
    if (roleAccess.canSeeOrders) {
        items.push({
            type: ItemType.item,
            key: "orders",
            text: t("settingsPanel.orders"),
            icon: <ShoppingBag />,
        });
    }

    if (roleAccess.canSeeAgent || roleAccess.canSeeProjects || roleAccess.canSeeApiKeys || roleAccess.canSeeFileManagement || hasTenderToolsAccess) {
        items.push({
            type: ItemType.heading,
            key: "configurationHeading",
            text: t("settingsPanel.configurationHeading"),
        });
    }

    if (roleAccess.canSeeAgent) {
        // Die Reiter des Bereichs (`AgentPanel`, `?tab=`) stehen zusätzlich als Untermenü in der
        // Seitenleiste. Ohne `tab` zeigt das Panel den Marktplatz.
        const activeAgentTab = selectedPanel === "agent" ? searchParams.get("tab") || "marketplace" : undefined;
        const agentTabs: Array<{ tab: AgentTabKey; text: string; guideId?: GuideId }> = [
            { tab: "marketplace", text: t("settingsPanel.agentSubmenu.marketplace") },
            { tab: "agentToolset", text: t("settingsPanel.agentSubmenu.agentToolset") },
            { tab: "documentPacks", text: t("settingsPanel.agentSubmenu.documentPacks") },
            { tab: "promptTools", text: t("settingsPanel.agentSubmenu.promptTools") },
            { tab: "agenticWorkflows", text: t("settingsPanel.agentSubmenu.agenticWorkflows") },
            ...(roleAccess.canSeeApiKeys ? [{ tab: "apiKeys" as const, text: t("settingsPanel.agentSubmenu.apiKeys"), guideId: "settings.agent.tab.apiKeys" as const }] : []),
        ];
        items.push({
            type: ItemType.item,
            key: "agent",
            text: t("settingsPanel.agent"),
            icon: <Bot />,
            children: agentTabs.map(({ tab, text, guideId }) => ({
                key: tab,
                text,
                guideId,
                active: activeAgentTab === tab,
                // Wie der frühere Reiterwechsel: Angebots- und Filterseiten des Marktplatzes fallen weg.
                action: () => openPanel("agent", { tab, offer: null, ...Object.fromEntries(FILTER_PARAMS.map((name) => [name, null])) }),
            })),
        });
    }

    if (roleAccess.canSeeProjects) {
        items.push({
            type: ItemType.item,
            key: "projects",
            text: t("settingsPanel.projects"),
            icon: <FolderKanban />,
        });
        // Die Heatmap mit Scoring, bislang nur unter CBOS: gleiche Seite, im Dashboard für alle mit Projekten.
        // Die FireIQ-Marke zeigt sie nicht (BRAND.scoringHeatmapEnabled).
        if (BRAND.scoringHeatmapEnabled) {
            items.push({
                type: ItemType.item,
                key: "scoringHeatmap",
                text: t("settingsPanel.scoringHeatmap"),
                icon: <MapIcon />,
            });
        }
    }

    if (
        roleAccess.canSeeProductManagement &&
        (configuration.shouldSeeProductManagement(authentication?.customer) ||
            hasProductManagementAccess === true)
    ) {
        items.push({
            type: ItemType.item,
            key: "productManagement",
            text: t("settingsPanel.productManagement"),
            icon: <Package />,
        });
    }

    // Partner Management: nur Admins des Master-Mandanten (tri-server docs/partner-management.md).
    if (roleAccess.isCustomerAdmin && configuration.shouldSeePartnerManagement(authentication?.customer)) {
        items.push({
            type: ItemType.item,
            key: "partnerManagement",
            text: t("settingsPanel.partnerManagement"),
            icon: <Handshake />,
        });
    }

    if (roleAccess.canSeeFileManagement) {
        items.push({
            type: ItemType.item,
            key: "fileManagement",
            text: t("settingsPanel.fileManagement"),
            icon: <Folders />,
        });
    }

    if (hasTenderToolsAccess) {
        items.push({
            type: ItemType.item,
            key: "tenderSearchProfiles",
            text: t("settingsPanel.tenderSearchProfiles"),
            icon: <FileSearch />,
        });
    }

    // Prozessverwaltung / Operations: always visible for Administrator and Technik; die
    // Richtlinien-Werkstatt dazu für jeden Nutzer eines freigegebenen Mandanten.
    if (roleAccess.canSeeOperations || hasGuidelinesAccess) {
        items.push({
            type: ItemType.heading,
            key: "operationsHeading",
            text: t("settingsPanel.operationsHeading"),
        });
    }
    if (roleAccess.canSeeOperations) {
        items.push({
            type: ItemType.item,
            key: "telemetry",
            text: t("settingsPanel.telemetry"),
            icon: <Network />,
        });
        items.push({
            type: ItemType.item,
            key: "alerts",
            text: t("settingsPanel.alerts"),
            icon: <AlarmClock />,
        });
        items.push({
            type: ItemType.item,
            key: "assetInventory",
            text: t("settingsPanel.assetInventory"),
            icon: <Boxes />,
        });
        items.push({
            type: ItemType.item,
            key: "defects",
            text: t("settingsPanel.defects"),
            icon: <Wrench />,
        });
        items.push({
            type: ItemType.item,
            key: "operationalState",
            text: t("settingsPanel.operationalState"),
            icon: <RefreshCw />,
        });
        items.push({
            type: ItemType.item,
            key: "assetArchive",
            text: t("settingsPanel.assetArchive"),
            icon: <Archive />,
        });
        // Nur mit Bestellung des Angebots „FM-Baselining“ (oder als dessen Anbieter).
        if (hasBaseliningAccess === true) {
            items.push({
                type: ItemType.item,
                key: "baselining",
                text: t("settingsPanel.baselining"),
                icon: <Receipt />,
            });
        }
        items.push({
            type: ItemType.item,
            key: "processManagement",
            text: t("settingsPanel.processManagement"),
            icon: <CalendarCog />,
        });

        if (hasOperatorObligationsAccess === true) {
            items.push({
                type: ItemType.item,
                key: "operatorObligations",
                text: t("settingsPanel.operatorObligations"),
                icon: <Scale />,
            });
        }
    }

    if (hasGuidelinesAccess) {
        items.push({
            type: ItemType.item,
            key: "guidelines",
            text: t("settingsPanel.guidelines"),
            icon: <BookOpenCheck />,
        });
    }

    if (roleAccess.canSeeApiKeys || roleAccess.canSeeDevelopment) {
        items.push({
            type: ItemType.heading,
            key: "developmentHeading",
            text: t("settingsPanel.developmentHeading"),
        });
    }

    if (roleAccess.canSeeApiKeys) {
        items.push({
            type: ItemType.item,
            key: "api",
            text: t("settingsPanel.api"),
            icon: <Cloud />,
        });
    }

    if (roleAccess.canSeeDataManagement && configuration.shouldSeeDevelopment(authentication)) {
        items.push({
            type: ItemType.item,
            key: "dataManagement",
            text: t("settingsPanel.dataManagement"),
            icon: <Globe />,
        });
    }

    // Ein Mandant mit `configuration.portalPanels` sieht nur die dort genannten Bereiche und die
    // immer sichtbaren — jeweils weiter hinter ihren Rollen- und Buchungs-Gates oben. Das blendet
    // aus, es sperrt nicht. Siehe docs/settings-sidebar-panels.md.
    items = restrictToPortalPanels(items, configuredPortalPanels(authentication?.customerObject));
    items = dropEmptyHeadings(items);

    // Auf /administration zeigt die Sidebar die Admin-Bereiche (Gates wie im
    // bisherigen AdministrationPage-Gerüst: Root-Admin sieht zusätzlich
    // Betreiberverantwortung, DIN-276-Baum, Nutzerverhalten und LLM-Forensik).
    if (onAdministration) {
        const isRootAdmin = roleAccess.isRootAdmin;
        items = [
            ...(isRootAdmin
                ? [
                      { type: ItemType.item, key: "operatorResponsibility", text: t("administrationPage.operatorResponsibility"), icon: <Scale /> },
                      { type: ItemType.item, key: "din276Tree", text: t("administrationPage.din276Tree"), icon: <Network /> },
                      { type: ItemType.item, key: "productImages", text: t("administrationPage.productImages"), icon: <Images /> },
              { type: ItemType.item, key: "documentAnalysis", text: t("administrationPage.documentAnalysis"), icon: <FileSearch /> },
                      { type: ItemType.item, key: "userBehavior", text: t("administrationPage.userBehavior"), icon: <Activity /> },
                      { type: ItemType.item, key: "llmForensics", text: "LLM-Forensik", icon: <FileSearch /> },
                      { type: ItemType.item, key: "synonyms", text: "Synonyme", icon: <Network /> },
                      { type: ItemType.item, key: "productSeries", text: t("administrationPage.productSeries"), icon: <Layers /> },
                      { type: ItemType.item, key: "demoTopics", text: "Demo-Termine", icon: <CalendarClock /> },
                      { type: ItemType.item, key: "leadsCockpit", text: t("administrationPage.leadsCockpit"), icon: <UserPlus /> },
                      { type: ItemType.item, key: "graphExplorer", text: "Graph-Explorer", icon: <Network /> },
                  ]
                : []),
            { type: ItemType.item, key: "customerManagement", text: t("administrationPage.customerManagement"), icon: <Building2 /> },
            { type: ItemType.item, key: "email", text: t("administrationPage.mail"), icon: <Mail /> },
            { type: ItemType.item, key: "billing", text: t("administrationPage.billing"), icon: <Wallet /> },
            { type: ItemType.item, key: "invoicing", text: t("administrationPage.invoicing"), icon: <CreditCard /> },
            { type: ItemType.item, key: "telemetry", text: t("administrationPage.telemetry"), icon: <FolderKanban /> },
            { type: ItemType.item, key: "monitoring", text: "Monitoring", icon: <LayoutDashboard /> },
            { type: ItemType.item, key: "statistics", text: t("administrationPage.statistics"), icon: <BarChart3 /> },
            { type: ItemType.item, key: "jobs", text: t("administrationPage.jobs"), icon: <Briefcase /> },
        ];
    }

    // Die CBOS-Seiten teilen sich die Shell mit einer eigenen, kurzen
    // Bereichsliste — die Einträge sind Routen, keine ?panel=-Parameter.
    if (onCbos) {
        items = [
            { type: ItemType.heading, key: "cbosHeading", text: "CBOS" },
            { type: ItemType.item, key: "portfolio-dashboard", text: t("portfolioDashboard.title"), icon: <LayoutDashboard /> },
            { type: ItemType.item, key: "heatmap", text: t("heatmap.title"), icon: <Flame /> },
        ];
    }

    return {
        items,
        selectedPanel,
        openPanel,
        hasOperatorObligationsAccess,
        hasProductManagementAccess,
        hasBaseliningAccess,
        hasTenderToolsAccess,
    };
}
